import { motion, AnimatePresence } from "motion/react";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import GradientText from "@/components/reactbits/GradientText";
import BlurText from "@/components/reactbits/BlurText";
import AnimatedContent from "@/components/reactbits/AnimatedContent";

const BACKDROP = "fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4";
const CARD =
  "relative w-full max-w-lg rounded-3xl border border-border bg-card p-8 text-center shadow-2xl";

function Backdrop({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      className={BACKDROP}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        className={CARD}
        initial={{ scale: 0.92, y: 24, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ type: "spring", damping: 22, stiffness: 260 }}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

export interface TourWelcomeProps {
  open: boolean;
  title: string;
  description: string;
  startLabel: string;
  skipLabel: string;
  onStart: () => void;
  onSkip: () => void;
}

export function TourWelcome({ open, title, description, startLabel, skipLabel, onStart, onSkip }: TourWelcomeProps) {
  return (
    <AnimatePresence>
      {open && (
        <Backdrop key="welcome">
          <motion.div
            className="mx-auto mb-5 flex h-16 w-16 items-center justify-center"
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", damping: 12, stiffness: 200, delay: 0.1 }}
          >
            <img src="/besthr.png" alt="BestHR" className="h-14 w-14 object-contain" />
          </motion.div>

          <h2 className="text-3xl font-extrabold tracking-tight">
            <GradientText>{title}</GradientText>
          </h2>

          <AnimatedContent delay={0.15}>
            <div className="mt-4 flex justify-center">
              <BlurText
                text={description}
                className="max-w-md justify-center text-center text-sm text-muted-foreground leading-relaxed"
              />
            </div>
          </AnimatedContent>

          <AnimatedContent delay={0.4}>
            <div className="mt-8 flex items-center justify-center gap-3">
              <Button variant="ghost" onClick={onSkip}>
                {skipLabel}
              </Button>
              <Button onClick={onStart} className="gap-1.5">
                {startLabel}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </AnimatedContent>
        </Backdrop>
      )}
    </AnimatePresence>
  );
}

export interface TourFinishProps {
  open: boolean;
  title: string;
  description: string;
  doneLabel: string;
  onDone: () => void;
}

export function TourFinish({ open, title, description, doneLabel, onDone }: TourFinishProps) {
  return (
    <AnimatePresence>
      {open && (
        <Backdrop key="finish">
          <motion.div
            className="mx-auto mb-5 flex h-16 w-16 items-center justify-center"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", damping: 10, stiffness: 220, delay: 0.05 }}
          >
            <img src="/besthr.png" alt="BestHR" className="h-14 w-14 object-contain" />
          </motion.div>

          <h2 className="text-3xl font-extrabold tracking-tight">
            <GradientText colors={["#10b981", "hsl(var(--primary))", "#10b981"]}>{title}</GradientText>
          </h2>

          <AnimatedContent delay={0.15}>
            <p className="mx-auto mt-4 max-w-md text-sm text-muted-foreground leading-relaxed">{description}</p>
          </AnimatedContent>

          <AnimatedContent delay={0.35}>
            <div className="mt-8 flex justify-center">
              <Button onClick={onDone} className="gap-1.5">
                <Check className="h-4 w-4" />
                {doneLabel}
              </Button>
            </div>
          </AnimatedContent>
        </Backdrop>
      )}
    </AnimatePresence>
  );
}
